import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import Sidebar from '../components/Sidebar.tsx'
import ChatArea, { ChatPartner } from '../components/ChatArea'
import ContactsPage from './ContactsPage'
import api from '../utils/api'
import '../styles/homePage.css'

interface Contact {
  id: string
  email: string
  prenom?: string
  nom?: string
  statut?: string
  photoUrl?: string
}

type SelectedChat = ChatPartner

interface HomePageProps {
  onLogout: () => void
}

export default function HomePage({ onLogout }: HomePageProps) {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<'discussions' | 'stories' | 'groups'>('discussions')
  const [selectedChat, setSelectedChat] = useState<SelectedChat | null>(null)
  const [showContactsPage, setShowContactsPage] = useState(false)
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false)
  const [groupName, setGroupName] = useState('')
  const [groupDescription, setGroupDescription] = useState('')
  const [availableContacts, setAvailableContacts] = useState<Contact[]>([])
  const [selectedMembers, setSelectedMembers] = useState<string[]>([])
  const [contactsLoading, setContactsLoading] = useState(false)
  const [groupError, setGroupError] = useState('')
  const [groupSuccess, setGroupSuccess] = useState('')
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768)
  const [sidebarRefreshTrigger, setSidebarRefreshTrigger] = useState(0)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const handleLogout = () => {
    onLogout()
    navigate('/')
  }

  const handleContactsClick = () => {
    setShowContactsPage(true)
  }

  const handleConversationSelect = (conversation: SelectedChat) => {
    setSelectedChat(conversation)
  }

  const handleChatGoBack = () => {
    setSelectedChat(null)
  }

  const refreshSidebar = () => {
    setSidebarRefreshTrigger(prev => prev + 1)
  }

  // Tracker les changements de taille d'écran
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768)
      setMobileMenuOpen(false) // Close menu on resize
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Close mobile menu when clicking outside
  useEffect(() => {
    if (!mobileMenuOpen) return

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (!target.closest('.home-header') && !target.closest('.mobile-menu')) {
        setMobileMenuOpen(false)
      }
    }

    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [mobileMenuOpen])

  useEffect(() => {
    if (!showCreateGroupModal) return

    const loadContacts = async () => {
      try {
        setContactsLoading(true)
        const response = await api.get('/api/contacts', {
          params: { email: localStorage.getItem('userEmail') }
        })
        const list = Array.isArray(response.data) ? response.data : []
        setAvailableContacts(list)
      } catch (err: any) {
        console.error('Erreur lors du chargement des contacts:', err)
      } finally {
        setContactsLoading(false)
      }
    }

    loadContacts()
  }, [showCreateGroupModal])

  const toggleMemberSelection = (email: string) => {
    setSelectedMembers((current) =>
      current.includes(email)
        ? current.filter((item) => item !== email)
        : [...current, email]
    )
  }

  const handleCreateGroup = async () => {
    if (!groupName.trim()) {
      setGroupError('Le nom du groupe est obligatoire.')
      return
    }

    try {
      setGroupError('')
      setGroupSuccess('')
      console.log('📝 Creating group with:', {
        name: groupName,
        description: groupDescription,
        createdBy: localStorage.getItem('userEmail'),
        members: selectedMembers
      })
      
      const response = await api.post('/api/groups', {
        name: groupName,
        description: groupDescription,
        createdBy: localStorage.getItem('userEmail'),
        members: selectedMembers
      })

      console.log('✅ Group creation response:', response.data)

      if (response.data?.group) {
        const group = response.data.group
        console.log('🎉 Group created successfully with ID:', group.id)
        setGroupSuccess('Groupe créé avec succès !')
        setShowCreateGroupModal(false)
        setGroupName('')
        setGroupDescription('')
        setSelectedMembers([])
        setActiveTab('groups')
        
        console.log('📌 Setting selectedChat with groupId:', group.id)
        setSelectedChat({
          type: 'group',
          id: group.id,
          name: group.name,
          description: group.description,
          photoUrl: group.photoUrl
        })
      }
    } catch (err: any) {
      console.error('❌ Erreur lors de la création du groupe:', err)
      setGroupError(err.response?.data?.error || err.message || 'Impossible de créer le groupe')
    }
  }

  return (
    <div className={`home-page ${isMobileView && selectedChat ? 'chat-active' : ''}`}>
      {/* Header */}
      <div className="home-header">
        <h1>🌹 MBOA Messenger</h1>
        <div className="header-actions">
          <button 
            className="btn-add-group"
            onClick={() => setShowCreateGroupModal(true)}
          >
            ➕ Créer un groupe
          </button>
          <button 
            className="btn-profile"
            onClick={() => navigate('/profile')}
          >
            👤 Mon Profil
          </button>
          <button 
            className="btn-logout"
            onClick={handleLogout}
          >
            🚪 Déconnexion
          </button>
        </div>

        {/* Mobile Menu Toggle Button */}
        <button 
          className="menu-toggle-btn"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          title="Menu"
        >
          ☰
        </button>
      </div>

      {/* Mobile Menu */}
      <div className={`mobile-menu ${mobileMenuOpen ? 'active' : ''}`}>
        <button 
          className="mobile-menu-item"
          onClick={() => {
            setShowCreateGroupModal(true)
            setMobileMenuOpen(false)
          }}
        >
          ➕ Créer un groupe
        </button>
        <button 
          className="mobile-menu-item"
          onClick={() => {
            navigate('/profile')
            setMobileMenuOpen(false)
          }}
        >
          👤 Mon Profil
        </button>
        <button 
          className="mobile-menu-item logout"
          onClick={() => {
            handleLogout()
            setMobileMenuOpen(false)
          }}
        >
          🚪 Déconnexion
        </button>
      </div>

      {/* Main Content */}
      <div className="home-content">
        {/* Sidebar */}
        {(!isMobileView || !selectedChat) && (
          <Sidebar
            onContactsClick={handleContactsClick}
            onTabChange={setActiveTab}
            activeTab={activeTab}
            onConversationSelect={handleConversationSelect}
            refreshTrigger={sidebarRefreshTrigger}
          />
        )}

        {/* Chat Area - Uniquement sur desktop ou quand un chat est sélectionné */}
        {!isMobileView && (
          <ChatArea 
            selectedContact={selectedChat}
            isMobileView={isMobileView}
            onGoBack={undefined}
            onMarkRead={refreshSidebar}
          />
        )}

        {/* Chat Area - Uniquement sur mobile quand un chat est sélectionné */}
        {isMobileView && selectedChat && (
          <ChatArea 
            selectedContact={selectedChat}
            isMobileView={isMobileView}
            onGoBack={handleChatGoBack}
            onMarkRead={refreshSidebar}
          />
        )}
      </div>

      {/* Contacts Page Modal */}
      {showContactsPage && (
        <ContactsPage
          onContactSelect={(contact) => {
            handleConversationSelect({
              type: 'user',
              id: contact.email,
              email: contact.email,
              prenom: contact.prenom,
              nom: contact.nom,
              statut: contact.statut,
              photoUrl: contact.photoUrl
            })
            setShowContactsPage(false)
          }}
          onClose={() => setShowContactsPage(false)}
        />
      )}

      {showCreateGroupModal && (
        <div className="group-modal-overlay">
          <div className="group-modal">
            <div className="group-modal-header">
              <h2>Créer un groupe</h2>
              <button className="btn-close" onClick={() => setShowCreateGroupModal(false)}>
                ✕
              </button>
            </div>
            <div className="group-modal-body">
              <label>
                Nom du groupe
                <input
                  type="text"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  placeholder="Nom du groupe"
                />
              </label>
              <label>
                Description (facultatif)
                <textarea
                  value={groupDescription}
                  onChange={(e) => setGroupDescription(e.target.value)}
                  placeholder="Description du groupe"
                  rows={3}
                />
              </label>
              <div className="group-members-selection">
                <h3>Sélectionnez des membres</h3>
                {contactsLoading ? (
                  <p>Chargement des contacts...</p>
                ) : availableContacts.length === 0 ? (
                  <p>Aucun contact disponible.</p>
                ) : (
                  <div className="group-members-list">
                    {availableContacts.map((contact) => (
                      <label key={contact.email} className={`member-item ${selectedMembers.includes(contact.email) ? 'selected' : ''}`}>
                        <input
                          type="checkbox"
                          checked={selectedMembers.includes(contact.email)}
                          onChange={() => toggleMemberSelection(contact.email)}
                        />
                        <span>{contact.prenom} {contact.nom} ({contact.email})</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              {groupError && <p className="form-error">{groupError}</p>}
              {groupSuccess && <p className="form-success">{groupSuccess}</p>}
            </div>
            <div className="group-modal-actions">
              <button className="btn-create-group" onClick={handleCreateGroup}>
                Créer le groupe
              </button>
              <button className="btn-cancel" onClick={() => setShowCreateGroupModal(false)}>
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
